import {json, redirect, type LoaderArgs} from '@shopify/remix-oxygen';
import {useLoaderData} from '@remix-run/react';
import {AnalyticsPageType} from '@shopify/hydrogen';
import {routeHeaders} from '~/data/cache';

//domains
import {PRODUCT_PER_PAGE_WITHOUT_ADS} from 'domains/collections';
import {
  parseNativeFilterParams,
  legacyBoostParamsToNative,
  extractPriceBounds,
  nativeProductToBfs,
} from 'domains/nativeCollection';
import {
  storefrontSearchRequest,
  searchSortToNative,
  resolveSearchOffsetCursor,
  SEARCH_PAGE_QUERY,
  SEARCH_FACETS_QUERY,
  type SearchConnection,
} from 'domains/nativeSearch';

//translation
import translation from 'translations/collection.json';
import tagTranslation from 'translations/tags.json';
import {routeLocale} from 'domains/global';

//components
import Container from 'components/Container';
import SearchMain from '~/components/search/SearchMain';

export const headers = routeHeaders;

const PLP_CACHE_CONTROL =
  'public, max-age=1, s-maxage=45, stale-while-revalidate=600';

export async function loader({params, request, context}: LoaderArgs) {
  const url = new URL(request.url);
  const searchParams = url.searchParams;

  const q = searchParams.get('q')?.trim();
  if (!q) {
    return redirect(params.lang ? `/${params.lang}` : '/');
  }

  // Old Boost filter params from indexed links → native params (q survives
  // untouched — it is not a pf_* param).
  const legacyParams = legacyBoostParamsToNative(searchParams);
  if (legacyParams) {
    const qs = legacyParams.toString();
    return redirect(`${url.pathname}${qs ? `?${qs}` : ''}`, 301);
  }

  const {country, language} = context.storefront.i18n;
  const env = context.env;

  const productPerPage = PRODUCT_PER_PAGE_WITHOUT_ADS;

  const parsed = parseNativeFilterParams(searchParams);
  const {sortKey, reverse} = searchSortToNative(parsed.sort);
  const offset = (parsed.page - 1) * productPerPage;

  const searchArgs = {
    env,
    q,
    filters: parsed.filters,
    sortKey,
    reverse,
    country,
    language,
  };

  const {after, outOfRange} = await resolveSearchOffsetCursor({
    ...searchArgs,
    offset,
  });

  const [pageData, priceBoundsData] = await Promise.all([
    storefrontSearchRequest<{search: SearchConnection}>(
      env,
      SEARCH_PAGE_QUERY,
      {
        q,
        filters: parsed.filters,
        sortKey,
        reverse,
        first: productPerPage,
        after,
        country,
        language,
      },
    ),
    parsed.hasPriceFilter
      ? storefrontSearchRequest<{search: SearchConnection}>(
          env,
          SEARCH_FACETS_QUERY,
          {
            q,
            filters: parsed.filters.filter((f) => !('price' in f)),
            country,
            language,
          },
        )
      : Promise.resolve(null),
  ]);

  const connection = pageData?.search;
  const facets = connection?.productFilters ?? [];
  const totalProducts = connection?.totalCount ?? 0;

  const priceBounds = extractPriceBounds(
    priceBoundsData?.search?.productFilters ?? facets,
  );

  const products = (outOfRange ? [] : connection?.nodes ?? []).map(
    nativeProductToBfs,
  );

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  const seo = {
    title: language === 'EN' ? `Search: ${q}` : `搜尋：${q}`,
    description: '',
    // Internal search results are thin, unbounded and duplicate the collection
    // pages. Keep them crawlable so the product links are still followed, but
    // out of the index — they were index,follow with a canonical pointing at
    // /search, which itself redirects to the homepage.
    robots: {noIndex: true, noFollow: false},
  };

  return json(
    {
      analytics: {
        pageType: AnalyticsPageType.collection,
      },
      translation: translation[selectedLocale],
      tagTranslation: tagTranslation[selectedLocale],
      seo,
      products,
      nativeFilters: {
        facets,
        priceBounds,
      },
      totalProducts,
    },
    {
      headers: {
        'Cache-Control': PLP_CACHE_CONTROL,
      },
    },
  );
}

export default function Collection() {
  const {products} = useLoaderData<typeof loader>();

  if (!products) {
    return <></>;
  }

  return (
    <section className="lg:pt-10 relative">
      <Container>
        <SearchMain />
      </Container>
    </section>
  );
}
